import { FC, useEffect, useState } from 'react';
import { CustomButton, CustomPopover } from 'components';
import { JobReviewStatus, JobReviewTabStatus } from '../../constants';
import { DropDownChevronDownIcon } from 'assets/icons';
import { ButtonType } from 'components/custom-button/CustomButton';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectSelectedReviewTab } from '../confirmJobsSlice';
import { getJobReviewStatusMap } from '../../utils';
import { getNotifications, selectCurrentTeamAccountingPermissions, showNotifier } from 'containers/app/appSlice';
import { InfoMesssages } from 'types/infoMessages';
import { NotifierTypes } from 'constants/notifierConstants';

interface StatusUpdatePopoverProps {
  onStatusChange: (status: string) => void;
  currentStatus?: string;
  onShowConfirmModal: (status: string) => void;
  disabled?: boolean;
  hasDraftAdjustment: boolean;
}

const StatusUpdatePopover: FC<StatusUpdatePopoverProps> = ({
  onStatusChange,
  currentStatus,
  onShowConfirmModal,
  disabled = false,
  hasDraftAdjustment = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedReviewTab = useAppSelector(selectSelectedReviewTab);
  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { approveAndProcessAccounting } = currentTeamAccountingPermissions ?? {};
  const notifications = useAppSelector(getNotifications);
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Set default status based on the selected tab
    if (selectedReviewTab === JobReviewTabStatus.NEEDS_REVIEW) {
      onStatusChange(JobReviewStatus.IN_PROGRESS);
    } else if (selectedReviewTab === JobReviewTabStatus.IN_PROGRESS) {
      onStatusChange(JobReviewStatus.CONFIRMED);
    } else {
      onStatusChange(getStatusOptions()[0].value);
    }
  }, [selectedReviewTab]);

  const getStatusOptions = () => {
    const relevantStatuses = [
      JobReviewStatus.IN_PROGRESS,
      JobReviewStatus.CONFIRMED,
      JobReviewStatus.SKIPPED,
      JobReviewStatus.NEEDS_REVIEW
    ];

    const allOptions = relevantStatuses.map(status => ({
      value: status,
      label: getJobReviewStatusMap(status).label
    }));

    return allOptions.filter(option => option.value !== (selectedReviewTab as string));
  };

  const handleTogglePopover = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const showInfoNotifier = () => {
    const notification = notifications.find(n => n.id === 'job-review-actions');
    if (notification) {
      return;
    }
    dispatch(
      showNotifier({
        id: 'job-review-actions',
        type: NotifierTypes.WARNING,
        message: { primaryMessage: 'You cannot confirm the jobs because some have pending adjustments.' },
        showClose: true
      })
    );
  };

  const handleStatusSelect = (status: string) => {
    if (hasDraftAdjustment && status === JobReviewStatus.CONFIRMED) {
      showInfoNotifier();
      return;
    }
    onStatusChange(status);
    setIsOpen(false);
    onShowConfirmModal(status);
  };

  const handlePrimaryButtonClick = () => {
    // For needs review tab, always mark as in progress
    if (selectedReviewTab === JobReviewTabStatus.NEEDS_REVIEW) {
      onShowConfirmModal(JobReviewStatus.IN_PROGRESS);
    }
    // For in progress tab, always mark as confirmed
    else if (selectedReviewTab === JobReviewTabStatus.IN_PROGRESS) {
      onShowConfirmModal(JobReviewStatus.CONFIRMED);
    }
    // For other tabs, use selected status
    else if (currentStatus) {
      onShowConfirmModal(currentStatus);
    }
  };

  const getButtonText = () => {
    if (selectedReviewTab === JobReviewTabStatus.NEEDS_REVIEW) {
      return 'Mark as In Progress';
    } else if (selectedReviewTab === JobReviewTabStatus.IN_PROGRESS) {
      return 'Mark as Confirmed';
    }
    return 'Mark as';
  };

  return (
    <div className="relative flex">
      {selectedReviewTab === JobReviewTabStatus.NEEDS_REVIEW || selectedReviewTab === JobReviewTabStatus.IN_PROGRESS ? (
        <>
          <CustomButton
            showDisabledReason={!approveAndProcessAccounting}
            disabledReason={InfoMesssages.noPermissionToApproveAndProcessAccounting}
            id="status-update"
            type={ButtonType.TERTIARY}
            onClick={handlePrimaryButtonClick}
            disabled={disabled}
            className="w-[190px] rounded-r-none border-none !bg-[#A4D7FA] px-4"
            childrenContainerStyles="text-17px font-semibold text-primaryText">
            {getButtonText()}
          </CustomButton>

          <CustomPopover
            anchorComponent={() => (
              <div className="flex w-full">
                <CustomButton
                  id="status-options"
                  type={ButtonType.TERTIARY}
                  onClick={handleTogglePopover}
                  disabled={disabled}
                  className={`rounded-l-none border-l border-accentText !bg-[#A4D7FA] py-1 px-3 ${
                    !approveAndProcessAccounting ? 'opacity-50' : ''
                  }`}>
                  <DropDownChevronDownIcon className="path path-fill-current h-2 w-2 text-primaryText" />
                </CustomButton>
              </div>
            )}
            disabled={!approveAndProcessAccounting}
            show={isOpen}
            onTogglePopover={handleTogglePopover}
            onClosePopover={() => setIsOpen(false)}
            width="w-[180px]"
            margin="mt-1"
            offset="-right-1"
            padding="px-0 py-1">
            <div className="flex flex-col">
              {getStatusOptions().map(option => {
                return (
                  <button
                    key={option.value}
                    className="w-full px-4 py-2 text-left text-sbase font-semibold text-primaryText hover:bg-gray-100"
                    onClick={() => handleStatusSelect(option.value)}>
                    <span className="mr-2 text-sm" style={{ color: getJobReviewStatusMap(option.value).text }}>
                      •
                    </span>{' '}
                    {option.label}
                  </button>
                );
              })}
            </div>
          </CustomPopover>
        </>
      ) : (
        <CustomPopover
          anchorComponent={() => (
            <div className="flex w-full">
              <CustomButton
                showDisabledReason={!approveAndProcessAccounting}
                disabledReason={InfoMesssages.noPermissionToApproveAndProcessAccounting}
                id="status-update"
                type={ButtonType.TERTIARY}
                onClick={handleTogglePopover}
                disabled={disabled}
                className="w-[130px] border-none !bg-[#A4D7FA] px-4"
                childrenContainerStyles="text-17px font-semibold text-primaryText">
                <div className="flex items-center">
                  <span className="shrink-0">Mark as</span>
                  <DropDownChevronDownIcon className="path path-fill-current mt-1 ml-2 h-3 w-3 text-primaryText" />
                </div>
              </CustomButton>
            </div>
          )}
          disabled={!approveAndProcessAccounting}
          show={isOpen}
          onTogglePopover={handleTogglePopover}
          onClosePopover={() => setIsOpen(false)}
          width="w-[180px]"
          margin="mt-1"
          offset="-right-1"
          padding="px-0 py-1">
          <div className="flex flex-col">
            {getStatusOptions().map(option => {
              return (
                <button
                  key={option.value}
                  className="w-full px-4 py-2 text-left text-sbase font-semibold text-primaryText hover:bg-gray-100"
                  onClick={() => handleStatusSelect(option.value)}>
                  <span className="mr-2 text-sm" style={{ color: getJobReviewStatusMap(option.value).text }}>
                    •
                  </span>{' '}
                  {option.label}
                </button>
              );
            })}
          </div>
        </CustomPopover>
      )}
    </div>
  );
};

export default StatusUpdatePopover;
